import useQueryParams from "@/hooks/use-query-params";
import { GENERIC_CLIENT, useMcpClientName } from "@/features/login/mcp-client-name";
import McpAccessDestination from "@/features/login/mcp-access-destination";
import { cn } from "@/lib/utils";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import { CopyButton } from "@blueskydataplatform/shared-ui/components/ui/copy-button";
import { ArrowRight, BookOpen, Check, Copy, Database, Loader2, Mail, Play, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Provider } from "@blueskydataplatform/shared-ui/types/providers.types";
import { useState } from "react";

export type AccountData = {
  email?: string;
  name?: string;
  tel?: string;
  company?: string;
  datasets?: string[];
  origin: string;
  isUser?: boolean;
  orgType?: string;
  provider?: Provider;
};

const CATALOG_PLANS_HREF = "/console/marketplace";
const CONSOLE_DASHBOARD_HREF = "/console";

export type MCPRegistrationConfig = {
  /** Display name of the MCP provider (e.g. "MT Newswires") */
  providerName: string;
  /** Length of the free trial in days */
  trialDays: number;
  /** One line naming what the user holds right now */
  holdings: React.ReactNode;
  /** Prompts the user can paste straight into their MCP client */
  starterPrompts: readonly string[];
  /** Heading above the capability grid */
  pillarsHeading: string;
  /** Below-the-fold capability summary. Each pillar shows a prompt that proves it, or a link out. */
  pillars: readonly {
    icon: LucideIcon;
    title: string;
    tagline: string;
    description: string;
    examplePrompt?: string;
    link?: { label: string; href: string };
  }[];
  /** Optional explainer video. Linked out rather than embedded — CSP frame-src excludes YouTube. */
  video?: { title: string; description: string; href: string; thumbnailUrl: string };
  /** Documentation for the access layer itself, for the user who wants to know how this works */
  docs?: { title: string; description: string; href: string };
  /** What happens when the trial ends */
  afterTrialNote: string;
  /** Custom support email (default: support@viaNexus.com) */
  supportEmail?: string;
};

type MCPRegistrationProps = {
  accountData: AccountData;
  config: MCPRegistrationConfig;
  onFinish: () => void | Promise<void>;
};

/** A prompt the user can lift straight into their client. */
const PromptRow = ({
  prompt,
  isCopied,
  onCopy,
  size = "md",
}: {
  prompt: string;
  isCopied: boolean;
  onCopy: () => void;
  size?: "md" | "sm";
}) => (
  <div
    className={cn(
      "border-border bg-muted/50 hover:border-primary-300 hover:bg-accent group flex items-center gap-2.5 rounded-xl border pr-1.5 transition-colors",
      size === "md" ? "py-2.5 pr-2 pl-4" : "py-1.5 pl-3"
    )}
  >
    <span className="text-primary shrink-0 font-mono text-sm select-none">&rsaquo;</span>
    <span className={cn("text-foreground flex-1 font-mono leading-snug", size === "md" ? "text-[13px]" : "text-xs")}>
      {prompt}
    </span>
    <CopyButton
      variant="ghost"
      size={size === "md" ? "sm" : "xs"}
      value={prompt}
      aria-label={`Copy prompt: ${prompt}`}
      onCopy={onCopy}
      className="text-muted-foreground shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 max-sm:opacity-100"
    >
      {isCopied ? (
        <>
          <Check className="text-success" /> Copied
        </>
      ) : (
        <>
          <Copy /> Copy
        </>
      )}
    </CopyButton>
  </div>
);

/** One end of the connector diagram. */
const WireNode = ({ icon: Icon, label }: { icon: LucideIcon; label: string }) => (
  <div className="flex w-24 shrink-0 flex-col items-center gap-2.5 sm:w-28">
    <div className="border-primary-700 bg-primary-900/80 flex h-14 w-14 items-center justify-center rounded-2xl border backdrop-blur">
      <Icon className="text-primary-200 h-6 w-6" />
    </div>
    <span className="text-primary-300 truncate text-xs font-medium tracking-wide">{label}</span>
  </div>
);

const MCPRegistration = ({ accountData, config: cfg, onFinish }: MCPRegistrationProps) => {
  const { callbackUrl, state } = useQueryParams();
  const [isFinishing, setIsFinishing] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);

  const providerName = accountData.provider?.name ?? cfg.providerName;
  const clientName = useMcpClientName(state, callbackUrl);

  // Flip the row back to "Copy" so a stale "Copied" doesn't sit there for the rest of the session.
  const markCopied = (prompt: string) => {
    setCopiedPrompt(prompt);
    window.setTimeout(() => setCopiedPrompt(current => (current === prompt ? null : current)), 2000);
  };

  const handleContinue = async () => {
    setIsFinishing(true);
    try {
      await onFinish();
      // Leave the button disabled — form.submit() is navigating away.
    } catch {
      setIsFinishing(false);
    }
  };

  return (
    <div className="bg-background flex flex-1 flex-col">
      <section className="mcp-band relative overflow-hidden px-6 pt-16 pb-32 md:pt-20">
        <div className="mcp-grid pointer-events-none absolute inset-0" aria-hidden />

        <div className="relative mx-auto w-full max-w-2xl text-center">
          {/* The connector itself: two endpoints and a live wire. */}
          <div className="mcp-rise flex items-center justify-center">
            <WireNode icon={Database} label={providerName} />
            <div className="bg-primary-700 relative mx-1 h-px flex-1 sm:mx-2" aria-hidden>
              <span className="mcp-pulse bg-primary-300 ring-primary-400/25 absolute -top-[3px] h-1.5 w-1.5 rounded-full ring-4" />
            </div>
            <WireNode icon={Sparkles} label={clientName ?? "Your agent or LLM"} />
          </div>

          <div className="mcp-rise mt-12 [animation-delay:80ms]">
            <span className="border-success-700 bg-success-950/60 text-success-300 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold tracking-[0.14em] uppercase">
              <span className="bg-success-400 h-1.5 w-1.5 rounded-full" />
              Account ready
            </span>
            <h1 className="text-primary-50 mt-5 text-3xl font-extrabold tracking-tight text-balance md:text-5xl">
              Connect {clientName ?? GENERIC_CLIENT} to {providerName}?
            </h1>
            <p className="text-primary-200/90 mx-auto mt-4 max-w-lg text-balance">
              {clientName ?? "Your agent"} will be able to read your organization’s {providerName} data. Your{" "}
              {cfg.trialDays}-day free trial starts now — no credit card.
            </p>
          </div>

          <p className="mcp-rise border-primary-800 bg-primary-900/50 text-primary-100 mt-8 rounded-2xl border px-6 py-4 text-sm leading-relaxed backdrop-blur [animation-delay:160ms]">
            {cfg.holdings}
          </p>
        </div>
      </section>

      {/* Prompt deck — overlaps the band so the first thing below the fold is an action. */}
      <div className="relative z-10 -mt-20 px-6">
        <div className="mcp-rise border-border bg-card mx-auto w-full max-w-2xl rounded-3xl border p-6 shadow-2xl md:p-8 [animation-delay:240ms]">
          <p className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
            Paste into {clientName ?? GENERIC_CLIENT} to start
          </p>
          <ul className="mt-4 space-y-2">
            {cfg.starterPrompts.map(prompt => (
              <li key={prompt}>
                <PromptRow prompt={prompt} isCopied={copiedPrompt === prompt} onCopy={() => markCopied(prompt)} />
              </li>
            ))}
          </ul>

          <Button
            size="lg"
            onClick={handleContinue}
            disabled={isFinishing}
            aria-busy={isFinishing}
            className="mt-7 h-12 w-full rounded-xl text-base font-semibold"
          >
            {isFinishing ? (
              <>
                <Loader2 className="animate-spin" />
                Redirecting to {clientName ?? GENERIC_CLIENT}
              </>
            ) : (
              <>
                Allow and continue to {clientName ?? GENERIC_CLIENT}
                <ArrowRight />
              </>
            )}
          </Button>
          <McpAccessDestination className="text-muted-foreground mt-3 text-center text-xs" />
        </div>
      </div>

      <section className="px-6 pt-20 pb-14">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-foreground text-2xl font-bold tracking-tight text-balance md:text-3xl">
            {cfg.pillarsHeading}
          </h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {cfg.pillars.map(pillar => (
              <div
                key={pillar.title}
                className="border-border bg-card hover:border-primary-300 rounded-2xl border p-6 transition-colors md:p-7"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
                    <pillar.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-foreground text-lg font-semibold">{pillar.title}</h3>
                    <p className="text-primary mt-0.5 text-sm font-medium">{pillar.tagline}</p>
                  </div>
                </div>
                <p className="text-muted-foreground mt-4 text-sm leading-relaxed">{pillar.description}</p>

                {pillar.examplePrompt && (
                  <div className="mt-4">
                    <PromptRow
                      size="sm"
                      prompt={pillar.examplePrompt}
                      isCopied={copiedPrompt === pillar.examplePrompt}
                      onCopy={() => markCopied(pillar.examplePrompt ?? "")}
                    />
                  </div>
                )}

                {pillar.link && (
                  <a
                    href={pillar.link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary mt-4 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                  >
                    {pillar.link.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>

          {cfg.docs && (
            <a
              href={cfg.docs.href}
              target="_blank"
              rel="noreferrer"
              className="border-border bg-card hover:border-primary-300 group mt-4 flex flex-col gap-5 rounded-2xl border p-5 transition-colors sm:flex-row sm:items-center md:p-6"
            >
              <span className="bg-primary/10 text-primary flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl">
                <BookOpen className="h-6 w-6" />
              </span>
              <span className="block">
                <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
                  Docs
                </span>
                <span className="text-foreground mt-2 block text-lg font-semibold">{cfg.docs.title}</span>
                <span className="text-muted-foreground mt-1.5 block text-sm leading-relaxed">
                  {cfg.docs.description}
                </span>
                <span className="text-primary mt-3 inline-flex items-center gap-1.5 text-sm font-medium group-hover:underline">
                  Read the docs
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </span>
            </a>
          )}

          {cfg.video && (
            <a
              href={cfg.video.href}
              target="_blank"
              rel="noreferrer"
              className="border-border bg-card hover:border-primary-300 group mt-4 flex flex-col gap-5 rounded-2xl border p-5 transition-colors sm:flex-row sm:items-center md:p-6"
            >
              <span className="bg-muted relative aspect-video w-full shrink-0 overflow-hidden rounded-xl sm:w-56">
                <img src={cfg.video.thumbnailUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-background/90 text-primary flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-transform group-hover:scale-110">
                    <Play className="h-5 w-5 translate-x-px fill-current" />
                  </span>
                </span>
              </span>
              <span className="block">
                <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
                  Watch
                </span>
                <span className="text-foreground mt-2 block text-lg font-semibold">{cfg.video.title}</span>
                <span className="text-muted-foreground mt-1.5 block text-sm leading-relaxed">
                  {cfg.video.description}
                </span>
                <span className="text-primary mt-3 inline-flex items-center gap-1.5 text-sm font-medium group-hover:underline">
                  Watch on YouTube
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </span>
            </a>
          )}

          <p className="text-muted-foreground mt-10 text-sm leading-relaxed">
            {cfg.afterTrialNote}{" "}
            <a
              href={CATALOG_PLANS_HREF}
              target="_blank"
              rel="noreferrer"
              className="text-primary font-medium hover:underline"
            >
              Review plans
            </a>
            {" · "}
            <a
              href={CONSOLE_DASHBOARD_HREF}
              target="_blank"
              rel="noreferrer"
              className="text-primary font-medium hover:underline"
            >
              Explore the platform
            </a>
          </p>

          <div className="text-muted-foreground border-border mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-6 text-xs">
            <span className="inline-flex items-center gap-2">
              <Mail className="h-3.5 w-3.5" />
              We&apos;ve emailed you the full instructions.
            </span>
            {cfg.supportEmail && (
              <span>
                Questions?{" "}
                <a href={`mailto:${cfg.supportEmail}`} className="text-primary font-medium hover:underline">
                  {cfg.supportEmail}
                </a>
              </span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MCPRegistration;
